import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { BranchProvider } from "./contexts/BranchContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { NetworkProvider } from "./contexts/NetworkContext";
import { FeatureGuard } from "./components/subscription/FeatureGuard";
import ProtectedRoute from './components/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import { CacheProvider } from './components/CacheProvider';
import { PageLoader } from './components/shared/PageLoader';

// Critical path - load immediately (auth)
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Ultra-lightweight skeleton for public menu (inline styles, no CSS deps)
import { InlineSkeleton } from './components/loading/InlineSkeleton';

// ... existing imports ...

// Lazy-loaded pages
const RipplefyLanding = lazy(() => import('./pages/RipplefyLanding'));
const PublicMenu = lazy(() => import("./pages/PublicMenu"));

// Lazy-loaded dashboard pages - load on demand
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const BusinessProfile = lazy(() => import("./pages/BusinessProfile"));
const Branches = lazy(() => import("./pages/Branches"));
const MenuManagement = lazy(() => import("./pages/MenuManagement"));
const Customization = lazy(() => import("./pages/Customization"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Feedback = lazy(() => import("./pages/Feedback"));
const QRCodes = lazy(() => import("./pages/QRCodes"));
const Settings = lazy(() => import("./pages/Settings"));
const Orders = lazy(() => import("./pages/Orders"));
const StaffManagement = lazy(() => import('./pages/StaffManagement'));
const AdminSubscription = lazy(() => import("./pages/AdminSubscription"));
const MetaPixelPage = lazy(() => import("./pages/MetaPixel"));
const WaitingList = lazy(() => import("./pages/WaitingList"));
const Subscription = lazy(() => import("./pages/Subscription"));
const CustomerFeedback = lazy(() => import("./pages/CustomerFeedback"));
const RiderDashboard = lazy(() => import("./pages/RiderDashboard"));
const Reservations = lazy(() => import("./pages/Reservations"));
const KitchenDisplay = lazy(() => import("./pages/KitchenDisplay"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogs"));
const MenuBuilder = lazy(() => import("./pages/MenuBuilder"));
const MenuDesigner = lazy(() => import("./pages/MenuDesigner"));
const VisualMapper = lazy(() => import("./pages/admin/VisualMapper"));

// Lazy-loaded components
const LoyaltyProgram = lazy(() => import("@/components/LoyaltyProgram").then(m => ({ default: m.LoyaltyProgram })));
const AdvancedAnalyticsDashboard = lazy(() => import("@/components/AdvancedAnalyticsDashboard").then(m => ({ default: m.AdvancedAnalyticsDashboard })));
const DashboardLayout = lazy(() => import('@/components/DashboardLayout').then(m => ({ default: m.DashboardLayout })));
const BranchSettingsWrapper = lazy(() => import('@/components/BranchSettingsWrapper').then(m => ({ default: m.BranchSettingsWrapper })));
const MaterialsList = lazy(() => import('./components/inventory/MaterialsList').then(m => ({ default: m.MaterialsList })));
const InventoryPage = lazy(() => import('./components/inventory/InventoryPage').then(m => ({ default: m.InventoryPage })));
const CommunicationDashboard = lazy(() => import("./components/communication/CommunicationDashboard").then(m => ({ default: m.CommunicationDashboard })));
const CommunicationSettings = lazy(() => import("./components/communication/CommunicationSettings").then(m => ({ default: m.CommunicationSettings })));
const FinancialDashboard = lazy(() => import("./components/financial/FinancialDashboard").then(m => ({ default: m.FinancialDashboard })));
const DeliveryManagement = lazy(() => import("./components/delivery/DeliveryManagement").then(m => ({ default: m.DeliveryManagement })));
const MarketingDashboard = lazy(() => import("./components/marketing/MarketingDashboard").then(m => ({ default: m.MarketingDashboard })));
const TableManagement = lazy(() => import("./components/tables/TableManagement").then(m => ({ default: m.TableManagement })));
const PublicReservation = lazy(() => import("./pages/public/PublicReservation"));

// Non-lazy imports for critical UI components
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { useRestaurant } from '@/hooks/useRestaurant';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { InstallPrompt } from "./components/InstallPrompt";
import { useSplashScreen } from "./components/SplashScreen";

// Debug panel for offline testing (only in development)
const OfflineDebugPanel = lazy(() => import('./components/desktop/OfflineDebugPanel'));

const queryClient = new QueryClient();

const AppRoutes = () => {
  const { i18n } = useTranslation();

  // Wrapper component for POS System
  const POSSystemPage = () => {
    const { branches } = useFilteredBranches();
    const defaultBranch = branches[0]; // Use first branch or add branch selection logic

    if (!defaultBranch) {
      return (
        <DashboardLayout>
          <div className="p-6">
            <Card>
              <CardHeader>
                <CardTitle>No Branch Available</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Please create a branch first to use the POS system.</p>
              </CardContent>
            </Card>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardHeader>
              <CardTitle>POS System</CardTitle>
            </CardHeader>
            <CardContent>
              <p>POS functionality is available in the main dashboard.</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  };

  // Wrapper component for Loyalty Program
  const LoyaltyPage = () => {
    const { restaurant } = useRestaurant();

    if (!restaurant) {
      return (
        <DashboardLayout>
          <div className="p-6">
            <Card>
              <CardHeader>
                <CardTitle>No Restaurant Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Please create your restaurant profile first to use the loyalty program.</p>
              </CardContent>
            </Card>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="p-6">
          <LoyaltyProgram restaurantId={restaurant.id} currency={restaurant.currency || 'USD'} />
        </div>
      </DashboardLayout>
    );
  };

  // Wrapper component for Advanced Analytics
  const AdvancedAnalyticsPage = () => {
    const { restaurant } = useRestaurant();

    if (!restaurant) {
      return (
        <DashboardLayout>
          <div className="p-6">
            <Card>
              <CardHeader>
                <CardTitle>No Restaurant Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Please create your restaurant profile first to view advanced analytics.</p>
              </CardContent>
            </Card>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="p-6">
          <AdvancedAnalyticsDashboard restaurantId={restaurant.id} currency={restaurant.currency || 'USD'} />
        </div>
      </DashboardLayout>
    );
  };

  return (
    <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} className="min-h-screen">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<RipplefyLanding />} />
          <Route path="/old-landing" element={<Index />} />
          <Route path="/auth" element={<Auth />} />

          {/* Rider Dashboard - Separate authentication */}
          <Route path="/rider-dashboard" element={<RiderDashboard />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/business-profile"
            element={
              <ProtectedRoute>
                <BusinessProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/branches"
            element={
            <ProtectedRoute>
              <Branches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/menu"
          element={
            <ProtectedRoute>
              <MenuManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/waiting-list"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <FeatureGuard feature="module_waiting_list" showBanner>
                    <WaitingList />
                  </FeatureGuard>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/reservations"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <FeatureGuard feature="module_reservations" showBanner>
                  <Reservations />
                </FeatureGuard>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/pos"
          element={
            <ProtectedRoute>
              <POSSystemPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/staff-management"
          element={
            <ProtectedRoute>
              <StaffManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/loyalty"
          element={
            <ProtectedRoute>
              <FeatureGuard feature="loyalty_tier" showBanner>
                <LoyaltyPage />
              </FeatureGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/advanced-analytics"
          element={
            <ProtectedRoute>
              <FeatureGuard feature="analytics_tier" requiredTier="advanced" showBanner>
                <AdvancedAnalyticsPage />
              </FeatureGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/customization"
          element={
            <ProtectedRoute>
              <Customization />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/menu-builder"
          element={
            <ProtectedRoute>
              <MenuBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/menu-designer"
          element={
            <ProtectedRoute>
              <MenuDesigner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/analytics"
          element={
            <ProtectedRoute>
              <FeatureGuard feature="analytics_tier" showBanner>
                <Analytics />
              </FeatureGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/feedback"
          element={
            <ProtectedRoute>
              <Feedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/qr-codes"
          element={
            <ProtectedRoute>
              <FeatureGuard feature="qr_code_ordering" showBanner>
                <QRCodes />
              </FeatureGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/tables"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <TableManagement />
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/kitchen"
          element={
            <ProtectedRoute>
              <FeatureGuard feature="kds_integration" showBanner>
                <KitchenDisplay />
              </FeatureGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/audit-logs"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AuditLogsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/meta-pixel"
          element={
            <ProtectedRoute>
              <MetaPixelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings"
          element={
            <ProtectedRoute requiresRestaurant>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/subscription"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <Subscription />
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/inventory"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <FeatureGuard feature="inventory_tier" showBanner>
                    <InventoryPage />
                  </FeatureGuard>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/communication"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <FeatureGuard feature="marketing_tier" showBanner>
                    <CommunicationDashboard />
                  </FeatureGuard>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/communication/settings"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <FeatureGuard feature="marketing_tier" showBanner>
                    <CommunicationSettings />
                  </FeatureGuard>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/financial"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <FeatureGuard feature="finance_tier" showBanner>
                    <FinancialDashboard />
                  </FeatureGuard>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/delivery"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <FeatureGuard feature="module_delivery" showBanner>
                    <DeliveryManagement />
                  </FeatureGuard>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/marketing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="p-6">
                  <FeatureGuard feature="marketing_tier" showBanner>
                    <MarketingDashboard />
                  </FeatureGuard>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/feedback/:token" element={<CustomerFeedback />} />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <Subscription />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/admin/subscription"
          element={
            <ProtectedRoute>
              <AdminSubscription />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/admin/visual-mapper"
          element={
            <ProtectedRoute>
              <VisualMapper />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/branches/:branchId/settings"
          element={
            <ProtectedRoute>
              <BranchSettingsWrapper />
            </ProtectedRoute>
          }
        />
        {/* Public QR Menu Route */}
        <Route 
          path="/menu/:slug" 
          element={
            <Suspense fallback={<InlineSkeleton />}>
              <PublicMenu />
            </Suspense>
          } 
        />
        {/* Public Reservation Route */}
        <Route path="/reserve/:slug" element={<PublicReservation />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </div>
  );
};

const App = () => {
  const { SplashComponent } = useSplashScreen(2500);
  // Don't show splash screen for public menu routes
  const isPublicMenu = window.location.pathname.startsWith('/menu/');

  return (
    <>
      {!isPublicMenu && SplashComponent}
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <NetworkProvider>
              <AuthProvider>
                <BranchProvider>
                  <SubscriptionProvider>
                    <CacheProvider>
                      <AppRoutes />
                      <Sonner />
                      <Toaster />
                      <InstallPrompt />
                      {/* Offline debug panel - only in development (Vite uses import.meta.env) */}
                      {import.meta.env.DEV && (
                        <Suspense fallback={null}>
                          <OfflineDebugPanel />
                        </Suspense>
                      )}
                    </CacheProvider>
                  </SubscriptionProvider>
                </BranchProvider>
              </AuthProvider>
            </NetworkProvider>
          </BrowserRouter>
        </TooltipProvider>
        <VercelAnalytics />
        <SpeedInsights />
      </QueryClientProvider>
    </>
  );
};

export default App;
